import { useState, useEffect, useCallback } from 'react';

export interface CartItem {
  id: number;
  name?: string;
  price?: number;
  image_url?: string;
  quantity: number;
  /** Présent si la ligne correspond à un pack produit */
  selected_pack_id?: string;
  selected_pack_duration?: string;
}

function packKey(packId?: string | null): string {
  return packId ?? '';
}

function sameCartLine(
  item: CartItem,
  productId: number,
  selectedPackId?: string | null
): boolean {
  return item.id === productId && packKey(item.selected_pack_id) === packKey(selectedPackId);
}

export const useCart = () => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);

  // Initialiser le panier au montage du composant
  useEffect(() => {
    // Charger le panier initial
    const stored = localStorage.getItem('cart');
    if (stored) {
      try {
        const items = JSON.parse(stored);
        // Valider et nettoyer les items invalides
        const validItems = Array.isArray(items) 
          ? items.filter((item: any) => 
              item && 
              typeof item.id === 'number' && 
              typeof item.quantity === 'number' && 
              item.quantity > 0
            )
          : [];
        
        // Si des items ont été supprimés, sauvegarder le panier nettoyé
        if (validItems.length !== items.length) {
          console.warn('Panier nettoyé: items invalides supprimés');
          localStorage.setItem('cart', JSON.stringify(validItems));
        }
        
        setCart(validItems);
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
        setCart([]);
        localStorage.removeItem('cart'); // Nettoyer le localStorage si corrompu
      }
    }
    
    // Écouter l'événement personnalisé cartUpdated
    const handleCartUpdate = (e: CustomEvent) => {
      if (e.detail) {
        // Valider les items reçus
        const validItems = Array.isArray(e.detail)
          ? e.detail.filter((item: any) => 
              item && 
              typeof item.id === 'number' && 
              typeof item.quantity === 'number' && 
              item.quantity > 0
            )
          : [];
        setCart(validItems);
      } else {
        // Recharger depuis localStorage si pas de détail
        const stored = localStorage.getItem('cart');
        if (stored) {
          try {
            const items = JSON.parse(stored);
            // Valider et nettoyer
            const validItems = Array.isArray(items)
              ? items.filter((item: any) => 
                  item && 
                  typeof item.id === 'number' && 
                  typeof item.quantity === 'number' && 
                  item.quantity > 0
                )
              : [];
            setCart(validItems);
          } catch (error) {
            console.error('Erreur lors du chargement du panier:', error);
            setCart([]);
          }
        } else {
          setCart([]);
        }
      }
    };

    // Écouter les changements de storage (autres onglets)
    const handleStorageChange = () => {
      const stored = localStorage.getItem('cart');
      if (stored) {
        try {
          const items = JSON.parse(stored);
          // Valider et nettoyer
          const validItems = Array.isArray(items)
            ? items.filter((item: any) => 
                item && 
                typeof item.id === 'number' && 
                typeof item.quantity === 'number' && 
                item.quantity > 0
              )
            : [];
          setCart(validItems);
        } catch (error) {
          console.error('Erreur lors du chargement du panier:', error);
          setCart([]);
          localStorage.removeItem('cart'); // Nettoyer si corrompu
        }
      } else {
        setCart([]);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cartUpdated', handleCartUpdate as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleCartUpdate as EventListener);
    };
  }, []);

  useEffect(() => {
    const total = cart.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = cart.reduce((sum, item) => {
      const price = item.price || 0;
      return sum + price * item.quantity;
    }, 0);
    setCartCount(total);
    setCartTotal(totalPrice);
  }, [cart]);

  const saveCart = useCallback((newCart: CartItem[]) => {
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    // Déclencher un événement personnalisé pour notifier les autres composants
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: newCart }));
  }, []);

  const addToCart = useCallback(async (productId: number, quantity: number = 1): Promise<boolean> => {
    try {
      // Charger les détails du produit depuis l'API si nécessaire
      const { productService } = await import('@/services/product.service');
      const product = await productService.getById(productId);

      return new Promise<boolean>((resolve) => {
        // Utiliser une fonction de mise à jour pour avoir l'état actuel
        setCart((currentCart) => {
          const existingItem = currentCart.find(
            (item) => item.id === productId && !item.selected_pack_id
          );
          let newCart: CartItem[];

          if (existingItem) {
            newCart = currentCart.map((item) =>
              item.id === productId && !item.selected_pack_id
                ? { ...item, quantity: item.quantity + quantity }
                : item
            );
          } else {
            newCart = [
              ...currentCart,
              {
                id: product.id,
                name: product.name,
                price: product.reduced_price || product.price,
                image_url: product.image_url || '/images/bottle.png',
                quantity,
              },
            ];
          }

          // Sauvegarder dans localStorage (synchrone)
          localStorage.setItem('cart', JSON.stringify(newCart));
          
          // Track le panier
          try {
            const { analyticsTracker } = require('@/services/analytics.service');
            const cartItems = newCart.map(item => ({
              id: item.id,
              name: item.name || '',
              price: item.price || 0,
              quantity: item.quantity,
            }));
            analyticsTracker.trackCart(cartItems);
          } catch (error) {
            console.error('Erreur tracking cart:', error);
          }
          
          // Déclencher un événement personnalisé
          window.dispatchEvent(new CustomEvent('cartUpdated', { detail: newCart }));
          
          // Résoudre la promesse après un court délai pour s'assurer que tout est mis à jour
          setTimeout(() => {
            resolve(true);
          }, 50);
          
          return newCart;
        });
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      return false;
    }
  }, []);

  const removeFromCart = useCallback(
    (productId: number, selectedPackId?: string | null) => {
      const newCart = cart.filter((item) => !sameCartLine(item, productId, selectedPackId));
      saveCart(newCart);
    },
    [cart, saveCart]
  );

  const updateQuantity = useCallback(
    (productId: number, quantity: number, selectedPackId?: string | null) => {
      if (quantity <= 0) {
        removeFromCart(productId, selectedPackId);
        return;
      }

      const newCart = cart.map((item) =>
        sameCartLine(item, productId, selectedPackId) ? { ...item, quantity } : item
      );
      saveCart(newCart);
    },
    [cart, saveCart, removeFromCart]
  );

  const clearCart = useCallback(() => {
    saveCart([]);
  }, [saveCart]);

  const getItemQuantity = useCallback((productId: number): number => {
    const item = cart.find(item => item.id === productId);
    return item?.quantity || 0;
  }, [cart]);

  return {
    cart,
    cartCount,
    cartTotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemQuantity,
  };
};
